import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AssignmentTurnedIn, PlaylistAddCheck, Explore, School } from '@mui/icons-material';
import SessionConstants from '../../config/SessionConstants.js';
import { clearViewState } from '../../utils/ProgramSummaryViewState.js';
import { Helmet } from "react-helmet";
import { Grid } from '@mui/material';
import ConfirmationModal from '../../uiComponents/ConfirmationModal.js';

import './index.css';

export default function Home() {
  const [showBiochemicalAlert, setShowBiochemicalAlert] = useState(false);
  const isBiochemicalDisabled = __ED3N_CONFIG__.disableBiochemical === true;
  const isClinicalDisabled = __ED3N_CONFIG__.disableClinical === true;
  const isBiochemicalAndClinicalDisabled = isBiochemicalDisabled && isClinicalDisabled;
  const featureNoticeTitle = isBiochemicalAndClinicalDisabled
    ? 'Biochemical and Clinical Feature Notice'
    : isBiochemicalDisabled
      ? 'Biochemical Feature Notice'
      : 'Clinical Feature Notice';
  const featureNoticeMessage = isBiochemicalAndClinicalDisabled
    ? 'The Biochemical and Clinical modules are currently not available to users at this time. These features will be available in the future.'
    : isBiochemicalDisabled
      ? 'The Biochemical module is currently not available to users at this time. This feature will be available in the future.'
      : 'The Clinical module is currently not available to users at this time. This feature will be available in the future.';

  useEffect(() => {
    // Check if any feature is disabled and alert hasn't been shown this session
    const hasShownAlert = sessionStorage.getItem(SessionConstants.BIOCHEMICAL_ALERT_SHOWN);
    if ((isBiochemicalDisabled || isClinicalDisabled) && !hasShownAlert) {
      setShowBiochemicalAlert(true);
    }
  }, [isBiochemicalDisabled, isClinicalDisabled]);

  const handleCloseAlert = () => {
    setShowBiochemicalAlert(false);
    sessionStorage.setItem(SessionConstants.BIOCHEMICAL_ALERT_SHOWN, 'true');
  };

  return (
    <>
      <Helmet>
        <title>ED3N - Home</title>
      </Helmet>
      <ConfirmationModal
        isOpen={showBiochemicalAlert}
        title={featureNoticeTitle}
        message={featureNoticeMessage}
        confirmText="OK"
        onConfirmation={handleCloseAlert}
      />
      <section>
        <div className="welcomeSection">
          <header>
            <h1 className="tagline">
              Enhancing Data-driven Disease Detection in Newborns (ED3N)
            </h1>
            <div className="description">
             ED3N serves as a secure, centralized
              resource for newborn screening partners to analyze and share
              biochemical and molecular data. ED3N supports laboratory
              best practices through standardized workflows and robust, validated
              analytical tools.
            </div>
          </header>
        </div>
        <div className="buttonsSection">
          <Grid container spacing={0}>
            <Grid item xs={12} sm={4}>
              <RouterLink
                className="landingButton card ds-7"
                to="/programsummary"
                onClick={clearViewState}
                state={{
                  programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
                }}
              >
                <div className="landingButtonIcon">
                  <PlaylistAddCheck alt="" fontSize="inherit" />
                </div>
                <div className="landingButtonTitle">Evaluate</div>
                <div className="landingButtonSubtitle">
                  Routine testing workflows for decision support
                </div>
                <div className="landingButtonText">
                  View and analyze your newborn screening data at the individual
                  patient level to enhance disease detection.
                </div>
              </RouterLink>
            </Grid>
            <Grid item xs={12} sm={4}>
              <RouterLink
                className="landingButton card ds-7"
                to="/explore"
                state={{
                  programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
                }}
              >
                <div className="landingButtonIcon">
                  <Explore alt="" fontSize="inherit" />
                </div>
                <div className="landingButtonTitle">Explore</div>
                <div className="landingButtonSubtitle">
                  Data repository for exploring correlations and trends
                </div>
                <div className="landingButtonText">
                  Explore de-identified, aggregate data. Ask what-if questions. Try
                  out new workflows before they are validated and moved to the
                  evaluate module.
                </div>
              </RouterLink>
            </Grid>
            <Grid item xs={12} sm={4}>
              <RouterLink
                className="landingButton card ds-7"
                to="/educate"
                state={{
                  programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
                }}
              >
                <div className="landingButtonIcon">
                  <School fontSize="inherit" alt="" />
                </div>
                <div className="landingButtonTitle">Educate</div>
                <div className="landingButtonSubtitle">
                  Helpful tutorials and training materials
                </div>
                <div className="landingButtonText">
                  Learn how to navigate ED3N. Improve your data analysis
                  skills. Find materials to train newborn screening personnel in
                  best practice workflows.
                </div>
              </RouterLink>
            </Grid>
          </Grid>
        </div>
      </section>
      <section>
        <div className="noticesSection">
          <h2 className="noticesHeading">NOTICES AND WARNINGS</h2>
          <div className="notice">
            NOTICE: Information in ED3N has not been
            cleared or approved by the FDA. The information in these databases
            is for RESEARCH USE ONLY. The results presented here should NOT be
            used for diagnosis, treatment, or assessment of patient health or
            management.
          </div>
          <div className="notice">
            WARNING: This warning banner provides privacy and security notices 
            consistent with applicable federal laws, directives, and other 
            federal guidance for accessing this Government system, which 
            includes all devices/storage media attached to this system. This 
            system is provided for Government-authorized use only. Unauthorized 
            or improper use of this system is prohibited and may result in 
            disciplinary action and/or civil and criminal penalties. At any 
            time, and for any lawful Government purpose, the government may 
            monitor, record, and audit your system usage and/or intercept, 
            search and seize any communication or data transiting or stored on 
            this system. Therefore, you have no reasonable expectation of 
            privacy. Any communication or data transiting or stored on this 
            system may be disclosed or used for any lawful Government purpose.
          </div>
        </div>
      </section>
    </>
  );
}